/**
 * React Query hooks for conversation history CRUD.
 *
 * Calls the backend's /api/conversations endpoints directly using the
 * absolute URL from getAssistantApiUrl. Auth is via X-Device-Token header
 * (anonymous device identity).
 *
 * These hooks provide:
 * - useConversationList(): Fetches paginated conversation list
 * - useConversationDetail(id): Fetches a single conversation with messages
 * - useDeleteConversation(): Mutation to delete a conversation
 * - useRenameConversation(): Mutation to rename a conversation
 *
 * Non-2xx bodies are normalised via `extractErrorMessage` (lib/api/
 * http-error.ts) — shared across every REST caller against this backend;
 * see that module's own doc comment for why a FastAPI 422's `detail` array
 * needs normalising at all (it used to render the literal text
 * "[object Object]").
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getDeviceToken } from '@/lib/device-token';
import type {
  ConversationListResponse,
  ConversationDetail,
} from '@/types/conversation';
import { getAssistantApiUrl } from '@/lib/assistant-config';
import { extractErrorMessage } from '@/lib/api/http-error';

// ─────────────────────────────────────────────────────────────────
// API base URL — always absolute. Resolves to
// NEXT_PUBLIC_ASSISTANT_BASE_URL when set, otherwise the prod
// CloudFront default (see src/lib/assistant-config.ts).
// ─────────────────────────────────────────────────────────────────

const API_BASE = getAssistantApiUrl('/api/conversations');

// ─────────────────────────────────────────────────────────────────
// Fetch helper with device token header
// ─────────────────────────────────────────────────────────────────

export async function conversationFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const deviceToken = getDeviceToken();
  if (!deviceToken) {
    throw new Error('Device token not available (SSR context)');
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Device-Token': deviceToken,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => ({}));
    const fallback = `API error: ${response.status} ${response.statusText}`;
    throw new Error(extractErrorMessage(error, fallback));
  }

  return response.json();
}

// ─────────────────────────────────────────────────────────────────
// Query keys — centralized for cache invalidation
// ─────────────────────────────────────────────────────────────────

export const conversationKeys = {
  all: ['conversations'] as const,
  list: () => [...conversationKeys.all, 'list'] as const,
  detail: (id: string) => [...conversationKeys.all, 'detail', id] as const,
};

// ─────────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────────

/**
 * Fetch the paginated conversation list for the current device.
 *
 * Automatically refetches when the query is stale (30s).
 * Returns { conversations, total, limit, offset }.
 */
export function useConversationList(limit = 20, offset = 0, enabled = true) {
  return useQuery({
    queryKey: [...conversationKeys.list(), { limit, offset }],
    queryFn: () =>
      conversationFetch<ConversationListResponse>(
        `?limit=${limit}&offset=${offset}`
      ),
    // Keep a short cache so reopening the history panel paints instantly from
    // cache — but always refetch in the background so the newest conversation
    // surfaces (#83). Without refetchOnMount:'always', a reopen within the
    // staleTime window served a stale list that predated the current chat.
    staleTime: 30_000,
    refetchOnMount: 'always', // panel opens (query mounts) → background refetch
    refetchOnWindowFocus: true, // regular refresh when the user returns
    enabled,
  });
}

/**
 * Fetch a single conversation with all decrypted messages.
 *
 * Used when resuming a conversation to display old messages.
 */
export function useConversationDetail(id: string | null, enabled = true) {
  return useQuery({
    queryKey: conversationKeys.detail(id ?? ''),
    queryFn: () => conversationFetch<ConversationDetail>(`/${id}`),
    enabled: enabled && !!id,
  });
}

// ─────────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────────

/**
 * Delete a conversation and all its messages.
 *
 * Invalidates the conversation list cache on success.
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) =>
      conversationFetch(`/${conversationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.list(),
      });
    },
    onError: () => {
      // Without this the list re-fetches and the row reappears with no
      // explanation — the user thinks the delete button is broken.
      toast.error("Couldn't delete the conversation. Please try again.");
    },
  });
}

/**
 * Rename a conversation.
 *
 * Uses optimistic update: immediately updates the cache, reverts
 * on failure. This makes the rename feel instant in the UI.
 */
export function useRenameConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      title,
    }: {
      conversationId: string;
      title: string;
    }) =>
      conversationFetch(`/${conversationId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      }),
    onMutate: async ({ conversationId, title }) => {
      // Cancel in-flight queries
      await queryClient.cancelQueries({
        queryKey: conversationKeys.list(),
      });

      // Snapshot previous value for rollback
      const previous = queryClient.getQueryData(conversationKeys.list());

      // Optimistically update the cache
      queryClient.setQueriesData(
        { queryKey: conversationKeys.list() },
        (old: ConversationListResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            conversations: old.conversations.map((c) =>
              c.id === conversationId ? { ...c, title } : c
            ),
          };
        }
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Rollback on failure
      if (context?.previous) {
        queryClient.setQueriesData(
          { queryKey: conversationKeys.list() },
          context.previous
        );
      }
      // The optimistic title silently snaps back otherwise — tell the user
      // why their edit reverted.
      toast.error("Couldn't rename the conversation. Please try again.");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: conversationKeys.list(),
      });
    },
  });
}
